import { isAnnotationObject, isBaseImageObject, isMaskObject } from './public-types.js';
import { markAnnotationObject, markBaseImageObject, markMaskObject } from './editor-object-kind.js';
import { ImageLoadTimeoutError, StateRestoreError } from './errors.js';
import { cloneResolvedImageFilterConfig, DEFAULT_IMAGE_FILTER_CONFIG, hasActiveImageFilters, normalizeImageFilterConfigSnapshot, } from './image-filter-config.js';
import { isSupportedImageDataUrl } from '../utils/file.js';
import { withTimeout } from '../utils/timeout.js';
const DEFAULT_MAX_RESTORE_CANVAS_PIXELS = 50000000;
const DEFAULT_MAX_RESTORE_CANVAS_DIMENSION = 16384;
const DEFAULT_MAX_SNAPSHOT_BYTES = 50 * 1024 * 1024;
const DEFAULT_MAX_SNAPSHOT_OBJECTS = 5000;
const DEFAULT_MAX_PUBLIC_RESTORE_NESTING_DEPTH = 100;
const DEFAULT_STATE_RESTORE_TIMEOUT_MS = 30000;
const PUBLIC_RESTORE_IMAGE_SOURCE_KEYS = new Set(['src', 'source']);
const PUBLIC_RESTORE_FABRIC_OBJECT_KEYS = new Set(['clipPath', 'backgroundImage', 'overlayImage']);
const PUBLIC_RESTORE_FABRIC_OBJECT_ARRAY_KEYS = new Set(['objects']);
const ALLOWED_PUBLIC_RESTORE_OBJECT_TYPES = new Set([
    'circle',
    'ellipse',
    'image',
    'line',
    'path',
    'polygon',
    'polyline',
    'rect',
    'text',
    'textbox',
]);
export const SNAPSHOT_CUSTOM_KEYS = Object.freeze([
    'editorObjectKind',
    'sessionObjectType',
    'maskId',
    'maskUid',
    'maskName',
    'isCropRect',
    'maskLabel',
    'originalAlpha',
    'originalStroke',
    'originalStrokeWidth',
    'hasControls',
    'selectable',
    'strokeUniform',
    'lockRotation',
    'transparentCorners',
    'borderColor',
    'cornerColor',
    'cornerSize',
    'flipX',
    'flipY',
    'isMosaicPreview',
    'annotationId',
    'annotationType',
    'shapeAnnotationKind',
    'annotationName',
    'annotationHidden',
    'annotationLocked',
    'annotationSelectable',
    'annotationEvented',
    'annotationHasControls',
    'annotationEditable',
    'overlayPersistentId',
    'overlayMetadata',
]);
function readFiniteNumber(value) {
    const numeric = Number(value);
    return Number.isFinite(numeric) ? numeric : null;
}
function serializedTypeMatches(liveObject, jsonObject) {
    const jsonType = typeof jsonObject.type === 'string' ? jsonObject.type.toLowerCase() : '';
    const liveType = typeof liveObject.type === 'string' ? liveObject.type.toLowerCase() : '';
    return !jsonType || liveType === jsonType;
}
function serializedPositionMatches(liveObject, jsonObject) {
    var _a, _b;
    const jsonLeft = readFiniteNumber(jsonObject.left);
    const jsonTop = readFiniteNumber(jsonObject.top);
    if (jsonLeft === null || jsonTop === null)
        return true;
    return (Math.abs(((_a = liveObject.left) !== null && _a !== void 0 ? _a : 0) - jsonLeft) < 0.5 &&
        Math.abs(((_b = liveObject.top) !== null && _b !== void 0 ? _b : 0) - jsonTop) < 0.5);
}
function serializedNumberMatches(liveValue, jsonValue, fallback, tolerance) {
    var _a;
    const jsonNumber = readFiniteNumber(jsonValue);
    if (jsonNumber === null)
        return true;
    const liveNumber = (_a = readFiniteNumber(liveValue)) !== null && _a !== void 0 ? _a : fallback;
    return Math.abs(liveNumber - jsonNumber) < tolerance;
}
function serializedTransformMatches(liveObject, jsonObject) {
    return (serializedNumberMatches(liveObject.angle, jsonObject.angle, 0, 0.5) &&
        serializedNumberMatches(liveObject.scaleX, jsonObject.scaleX, 1, 0.0001) &&
        serializedNumberMatches(liveObject.scaleY, jsonObject.scaleY, 1, 0.0001));
}
function serializedObjectMatches(liveObject, jsonObject) {
    const live = liveObject;
    if (typeof jsonObject.maskUid === 'string' && typeof live.maskUid === 'string') {
        return live.maskUid === jsonObject.maskUid;
    }
    if (typeof jsonObject.maskId === 'number' && typeof live.maskId === 'number') {
        return live.maskId === jsonObject.maskId;
    }
    if (typeof jsonObject.annotationId === 'number' && typeof live.annotationId === 'number') {
        return live.annotationId === jsonObject.annotationId;
    }
    if (typeof jsonObject.sessionObjectType === 'string' &&
        typeof live.sessionObjectType === 'string') {
        return live.sessionObjectType === jsonObject.sessionObjectType;
    }
    if (typeof jsonObject.editorObjectKind === 'string' &&
        typeof live.editorObjectKind === 'string' &&
        live.editorObjectKind !== jsonObject.editorObjectKind) {
        return false;
    }
    return (serializedTypeMatches(liveObject, jsonObject) &&
        serializedPositionMatches(liveObject, jsonObject) &&
        serializedTransformMatches(liveObject, jsonObject));
}
function findCanvasObjectForJson(canvasObjects, jsonObject, preferredIndex, consumedIndexes) {
    const preferred = canvasObjects[preferredIndex];
    if (preferred &&
        !consumedIndexes.has(preferredIndex) &&
        serializedObjectMatches(preferred, jsonObject)) {
        consumedIndexes.add(preferredIndex);
        return { object: preferred, index: preferredIndex };
    }
    const matchedIndex = canvasObjects.findIndex((candidate, index) => !consumedIndexes.has(index) && serializedObjectMatches(candidate, jsonObject));
    if (matchedIndex < 0)
        return null;
    consumedIndexes.add(matchedIndex);
    return { object: canvasObjects[matchedIndex], index: matchedIndex };
}
function copySnapshotCustomPropsFromCanvas(canvasObjects, jsonObjects) {
    if (!Array.isArray(jsonObjects))
        return;
    const consumedIndexes = new Set();
    for (let index = 0; index < jsonObjects.length; index += 1) {
        const jsonObject = jsonObjects[index];
        if (!jsonObject)
            continue;
        const match = findCanvasObjectForJson(canvasObjects, jsonObject, index, consumedIndexes);
        if (!match)
            continue;
        const liveObject = match.object;
        if (typeof liveObject.editorObjectKind === 'string') {
            jsonObject.editorObjectKind = liveObject.editorObjectKind;
        }
        if (typeof liveObject.sessionObjectType === 'string') {
            jsonObject.sessionObjectType = liveObject.sessionObjectType;
        }
        if (typeof liveObject.maskId === 'number')
            jsonObject.maskId = liveObject.maskId;
        if (typeof liveObject.maskUid === 'string')
            jsonObject.maskUid = liveObject.maskUid;
        if (typeof liveObject.maskName === 'string')
            jsonObject.maskName = liveObject.maskName;
        if (typeof liveObject.originalAlpha === 'number') {
            jsonObject.originalAlpha = liveObject.originalAlpha;
        }
        if (liveObject.originalStroke !== undefined) {
            jsonObject.originalStroke = liveObject.originalStroke;
        }
        if (typeof liveObject.originalStrokeWidth === 'number') {
            jsonObject.originalStrokeWidth = liveObject.originalStrokeWidth;
        }
        if (typeof liveObject.hasControls === 'boolean') {
            jsonObject.hasControls = liveObject.hasControls;
        }
        if (typeof liveObject.selectable === 'boolean') {
            jsonObject.selectable = liveObject.selectable;
        }
        if (typeof liveObject.strokeUniform === 'boolean') {
            jsonObject.strokeUniform = liveObject.strokeUniform;
        }
        if (typeof liveObject.lockRotation === 'boolean') {
            jsonObject.lockRotation = liveObject.lockRotation;
        }
        if (typeof liveObject.transparentCorners === 'boolean') {
            jsonObject.transparentCorners = liveObject.transparentCorners;
        }
        if (typeof liveObject.borderColor === 'string') {
            jsonObject.borderColor = liveObject.borderColor;
        }
        if (typeof liveObject.cornerColor === 'string') {
            jsonObject.cornerColor = liveObject.cornerColor;
        }
        if (typeof liveObject.cornerSize === 'number') {
            jsonObject.cornerSize = liveObject.cornerSize;
        }
        if (typeof liveObject.flipX === 'boolean') {
            jsonObject.flipX = liveObject.flipX;
        }
        if (typeof liveObject.flipY === 'boolean') {
            jsonObject.flipY = liveObject.flipY;
        }
        if (liveObject.isCropRect === true)
            jsonObject.isCropRect = true;
        if (liveObject.maskLabel === true)
            jsonObject.maskLabel = true;
        if (liveObject.isMosaicPreview === true)
            jsonObject.isMosaicPreview = true;
        if (typeof liveObject.annotationId === 'number') {
            jsonObject.annotationId = liveObject.annotationId;
        }
        if (typeof liveObject.annotationType === 'string') {
            jsonObject.annotationType = liveObject.annotationType;
        }
        if (typeof liveObject.shapeAnnotationKind === 'string') {
            jsonObject.shapeAnnotationKind = liveObject.shapeAnnotationKind;
        }
        if (typeof liveObject.annotationName === 'string') {
            jsonObject.annotationName = liveObject.annotationName;
        }
        if (typeof liveObject.annotationHidden === 'boolean') {
            jsonObject.annotationHidden = liveObject.annotationHidden;
        }
        if (typeof liveObject.annotationLocked === 'boolean') {
            jsonObject.annotationLocked = liveObject.annotationLocked;
        }
        if (typeof liveObject.annotationSelectable === 'boolean') {
            jsonObject.annotationSelectable = liveObject.annotationSelectable;
        }
        if (typeof liveObject.annotationEvented === 'boolean') {
            jsonObject.annotationEvented = liveObject.annotationEvented;
        }
        if (typeof liveObject.annotationHasControls === 'boolean') {
            jsonObject.annotationHasControls = liveObject.annotationHasControls;
        }
        if (typeof liveObject.annotationEditable === 'boolean') {
            jsonObject.annotationEditable = liveObject.annotationEditable;
        }
        if (typeof liveObject.overlayPersistentId === 'string') {
            jsonObject.overlayPersistentId = liveObject.overlayPersistentId;
        }
        if (liveObject.overlayMetadata !== undefined) {
            jsonObject.overlayMetadata = liveObject.overlayMetadata;
        }
    }
}
function isActiveSelectionObject(object) {
    if (!object)
        return false;
    const type = typeof object.type === 'string' ? object.type.toLowerCase() : '';
    if (type === 'activeselection')
        return true;
    const isType = object.isType;
    return (typeof isType === 'function' &&
        (isType.call(object, 'ActiveSelection') || isType.call(object, 'activeSelection')));
}
export function saveState(input) {
    var _a, _b, _c, _d;
    const { canvas, currentScale, currentRotation, baseImageScale } = input;
    const activeObject = (_b = (_a = canvas).getActiveObject) === null || _b === void 0 ? void 0 : _b.call(_a);
    const activeMaskId = activeObject && isMaskObject(activeObject)
        ? activeObject.maskId
        : typeof input.activeMaskId === 'number'
            ? input.activeMaskId
            : null;
    const activeAnnotationId = activeObject && isAnnotationObject(activeObject)
        ? activeObject.annotationId
        : typeof input.activeAnnotationId === 'number'
            ? input.activeAnnotationId
            : null;
    if (isActiveSelectionObject(activeObject)) {
        canvas.discardActiveObject();
    }
    const jsonObj = canvas.toJSON(SNAPSHOT_CUSTOM_KEYS);
    copySnapshotCustomPropsFromCanvas(canvas.getObjects(), jsonObj.objects);
    if (Array.isArray(jsonObj.objects)) {
        jsonObj.objects = jsonObj.objects.filter((o) => o.editorObjectKind !== 'session' &&
            o.isCropRect !== true &&
            o.maskLabel !== true &&
            o.isMosaicPreview !== true);
    }
    jsonObj._editorState = {
        currentScale,
        currentRotation,
        baseImageScale,
        currentImageMimeType: (_c = input.currentImageMimeType) !== null && _c !== void 0 ? _c : null,
        activeObjectKind: activeMaskId !== null ? 'mask' : activeAnnotationId !== null ? 'annotation' : null,
    };
    const imageFilterConfig = cloneResolvedImageFilterConfig((_d = input.imageFilterConfig) !== null && _d !== void 0 ? _d : DEFAULT_IMAGE_FILTER_CONFIG);
    if (hasActiveImageFilters(imageFilterConfig)) {
        jsonObj._editorState.imageFilterConfig = imageFilterConfig;
    }
    if (activeMaskId !== null)
        jsonObj._editorState.activeMaskId = activeMaskId;
    if (activeAnnotationId !== null) {
        jsonObj._editorState.activeAnnotationId = activeAnnotationId;
    }
    return JSON.stringify(jsonObj);
}
export async function loadFromState(input) {
    var _a, _b, _c, _d, _e, _f, _g;
    const { canvas, jsonString: snapshotInput, setCanvasSize } = input;
    const restoreTrustLevel = (_a = input.restoreTrustLevel) !== null && _a !== void 0 ? _a : 'public';
    const isPublicRestore = restoreTrustLevel === 'public';
    let jsonString;
    try {
        jsonString =
            typeof snapshotInput === 'string' ? snapshotInput : JSON.stringify(snapshotInput);
    }
    catch (error) {
        throw new StateRestoreError('loadFromState: snapshot JSON is malformed.', error);
    }
    if (isPublicRestore) {
        assertSnapshotByteSizeAllowed(jsonString, (_b = input.maxSnapshotBytes) !== null && _b !== void 0 ? _b : DEFAULT_MAX_SNAPSHOT_BYTES);
    }
    let json;
    try {
        json = JSON.parse(jsonString);
    }
    catch (error) {
        throw new StateRestoreError('loadFromState: snapshot JSON is malformed.', error);
    }
    if (isPublicRestore) {
        validatePublicSnapshot(json, {
            maxSnapshotObjects: (_c = input.maxSnapshotObjects) !== null && _c !== void 0 ? _c : DEFAULT_MAX_SNAPSHOT_OBJECTS,
        });
    }
    if (typeof json.width === 'number' &&
        json.width > 0 &&
        typeof json.height === 'number' &&
        json.height > 0) {
        assertRestoredCanvasSizeAllowed(json.width, json.height, (_d = input.maxCanvasPixels) !== null && _d !== void 0 ? _d : DEFAULT_MAX_RESTORE_CANVAS_PIXELS, isPublicRestore
            ? ((_e = input.maxRestoreCanvasDimension) !== null && _e !== void 0 ? _e : DEFAULT_MAX_RESTORE_CANVAS_DIMENSION)
            : null);
        setCanvasSize(json.width, json.height);
    }
    const loadFromJsonPromise = canvas.loadFromJSON(json);
    try {
        await withTimeout(loadFromJsonPromise, DEFAULT_STATE_RESTORE_TIMEOUT_MS, 'canvas.loadFromJSON');
    }
    catch (error) {
        if (error instanceof ImageLoadTimeoutError) {
            throw new StateRestoreError('loadFromState: canvas.loadFromJSON timed out while restoring editor state.', error);
        }
        throw error;
    }
    const objects = canvas.getObjects();
    restoreEditorObjectPropsFromJson(objects, (_f = json.objects) !== null && _f !== void 0 ? _f : []);
    const editorState = json._editorState && typeof json._editorState === 'object'
        ? {
            currentScale: typeof json._editorState.currentScale === 'number'
                ? json._editorState.currentScale
                : 1,
            currentRotation: typeof json._editorState.currentRotation === 'number'
                ? json._editorState.currentRotation
                : 0,
            baseImageScale: typeof json._editorState.baseImageScale === 'number'
                ? json._editorState.baseImageScale
                : 1,
        }
        : null;
    if (editorState && json._editorState && typeof json._editorState.activeMaskId === 'number') {
        editorState.activeMaskId = json._editorState.activeMaskId;
    }
    if (editorState &&
        json._editorState &&
        typeof json._editorState.activeAnnotationId === 'number') {
        editorState.activeAnnotationId = json._editorState.activeAnnotationId;
    }
    if (editorState && json._editorState && 'activeObjectKind' in json._editorState) {
        const kind = json._editorState.activeObjectKind;
        editorState.activeObjectKind =
            kind === 'mask' || kind === 'annotation' || kind === null ? kind : null;
    }
    if (editorState && json._editorState && 'currentImageMimeType' in json._editorState) {
        const mimeType = json._editorState.currentImageMimeType;
        editorState.currentImageMimeType =
            mimeType === 'image/jpeg' || mimeType === 'image/png' || mimeType === 'image/webp'
                ? mimeType
                : null;
    }
    if (editorState && json._editorState && 'imageFilterConfig' in json._editorState) {
        editorState.imageFilterConfig = normalizeImageFilterConfigSnapshot(json._editorState.imageFilterConfig);
    }
    const maxMaskId = objects
        .filter(isMaskObject)
        .reduce((max, maskObject) => Math.max(max, maskObject.maskId), 0);
    const maxAnnotationId = objects
        .filter(isAnnotationObject)
        .reduce((max, annotationObject) => Math.max(max, annotationObject.annotationId), 0);
    const masks = objects.filter(isMaskObject);
    const annotations = objects.filter(isAnnotationObject);
    const originalImage = (_g = objects.find(isBaseImageObject)) !== null && _g !== void 0 ? _g : null;
    return {
        editorState,
        maxMaskId,
        maxAnnotationId,
        originalImage,
        objects,
        masks,
        annotations,
        jsonString,
    };
}
function assertRestoredCanvasSizeAllowed(width, height, maxCanvasPixels, maxCanvasDimension) {
    const safeMaxCanvasPixels = Number.isFinite(maxCanvasPixels) && maxCanvasPixels > 0
        ? Math.floor(maxCanvasPixels)
        : DEFAULT_MAX_RESTORE_CANVAS_PIXELS;
    const safeMaxCanvasDimension = maxCanvasDimension !== null && Number.isFinite(maxCanvasDimension) && maxCanvasDimension > 0
        ? Math.floor(maxCanvasDimension)
        : null;
    if (safeMaxCanvasDimension !== null &&
        (width > safeMaxCanvasDimension || height > safeMaxCanvasDimension)) {
        throw new StateRestoreError(`loadFromState: snapshot canvas size ${width}x${height} exceeds maxRestoreCanvasDimension (${safeMaxCanvasDimension}).`);
    }
    const pixelCount = width * height;
    if (!Number.isFinite(pixelCount) || pixelCount > safeMaxCanvasPixels) {
        throw new StateRestoreError(`loadFromState: snapshot canvas size ${width}x${height} exceeds maxCanvasPixels (${safeMaxCanvasPixels}).`);
    }
}
function getUtf8ByteLength(value) {
    if (typeof TextEncoder === 'function') {
        return new TextEncoder().encode(value).byteLength;
    }
    return value.length;
}
function toPositiveIntegerLimit(value, fallback) {
    return Number.isFinite(value) && value > 0 ? Math.floor(value) : fallback;
}
function assertSnapshotByteSizeAllowed(jsonString, maxSnapshotBytes) {
    const safeMaxSnapshotBytes = toPositiveIntegerLimit(maxSnapshotBytes, DEFAULT_MAX_SNAPSHOT_BYTES);
    if (jsonString.length > safeMaxSnapshotBytes) {
        throw new StateRestoreError(`loadFromState: snapshot JSON size exceeds maxSnapshotBytes (${safeMaxSnapshotBytes}).`);
    }
    const worstCaseUtf8Bytes = jsonString.length * 3;
    if (worstCaseUtf8Bytes <= safeMaxSnapshotBytes)
        return;
    const byteLength = getUtf8ByteLength(jsonString);
    if (byteLength > safeMaxSnapshotBytes) {
        throw new StateRestoreError(`loadFromState: snapshot JSON size ${byteLength} bytes exceeds maxSnapshotBytes (${safeMaxSnapshotBytes}).`);
    }
}
function validatePublicSnapshot(json, options) {
    var _a;
    if (json.objects !== undefined && !Array.isArray(json.objects)) {
        throw new StateRestoreError('loadFromState: snapshot objects must be an array.');
    }
    const objects = (_a = json.objects) !== null && _a !== void 0 ? _a : [];
    const safeMaxSnapshotObjects = toPositiveIntegerLimit(options.maxSnapshotObjects, DEFAULT_MAX_SNAPSHOT_OBJECTS);
    if (objects.length > safeMaxSnapshotObjects) {
        throw new StateRestoreError(`loadFromState: snapshot contains ${objects.length} objects, exceeding maxSnapshotObjects (${safeMaxSnapshotObjects}).`);
    }
    const context = {
        maxSnapshotObjects: safeMaxSnapshotObjects,
        objectCount: 0,
        seen: new WeakSet(),
        countedFabricObjects: new WeakSet(),
    };
    objects.forEach((object, index) => validatePublicSnapshotValue(object, `objects[${index}]`, {
        validateFabricObject: true,
        allowEditorOwnedCustomMask: true,
        arrayEntriesAreFabricObjects: false,
    }, context, 0));
    for (const [key, value] of Object.entries(json)) {
        if (key === 'objects')
            continue;
        validatePublicSnapshotValue(value, key, {
            validateFabricObject: PUBLIC_RESTORE_FABRIC_OBJECT_KEYS.has(key),
            allowEditorOwnedCustomMask: false,
            arrayEntriesAreFabricObjects: PUBLIC_RESTORE_FABRIC_OBJECT_ARRAY_KEYS.has(key),
        }, context, 0);
    }
}
function validatePublicSnapshotValue(value, path, options, context, depth) {
    if (depth > DEFAULT_MAX_PUBLIC_RESTORE_NESTING_DEPTH) {
        throw new StateRestoreError(`loadFromState: snapshot field "${path}" exceeds max nested object depth (${DEFAULT_MAX_PUBLIC_RESTORE_NESTING_DEPTH}).`);
    }
    if (!value || typeof value !== 'object')
        return;
    const alreadySeen = context.seen.has(value);
    if (!alreadySeen)
        context.seen.add(value);
    if (options.validateFabricObject) {
        validatePublicSnapshotFabricObjectPayload(value, path, options.allowEditorOwnedCustomMask, context);
    }
    if (alreadySeen)
        return;
    if (Array.isArray(value)) {
        value.forEach((entry, entryIndex) => validatePublicSnapshotValue(entry, `${path}[${entryIndex}]`, {
            validateFabricObject: options.arrayEntriesAreFabricObjects,
            allowEditorOwnedCustomMask: false,
            arrayEntriesAreFabricObjects: false,
        }, context, depth + 1));
        return;
    }
    for (const [key, nestedValue] of Object.entries(value)) {
        const nestedPath = path ? `${path}.${key}` : key;
        if (typeof nestedValue === 'string' &&
            nestedValue.trim() !== '' &&
            isPublicRestoreImageSourceKey(key) &&
            !isSupportedImageDataUrl(nestedValue)) {
            throw new StateRestoreError(`loadFromState: snapshot field "${nestedPath}" must use a supported data URL source.`);
        }
        validatePublicSnapshotValue(nestedValue, nestedPath, {
            validateFabricObject: shouldValidatePublicRestoreNestedFabricObject(key, nestedValue),
            allowEditorOwnedCustomMask: false,
            arrayEntriesAreFabricObjects: PUBLIC_RESTORE_FABRIC_OBJECT_ARRAY_KEYS.has(key),
        }, context, depth + 1);
    }
}
function validatePublicSnapshotFabricObjectPayload(value, path, allowEditorOwnedCustomMask, context) {
    if (!value || typeof value !== 'object' || Array.isArray(value)) {
        throw new StateRestoreError(`loadFromState: snapshot field "${path}" is invalid.`);
    }
    if (!context.countedFabricObjects.has(value)) {
        context.countedFabricObjects.add(value);
        context.objectCount += 1;
        if (context.objectCount > context.maxSnapshotObjects) {
            throw new StateRestoreError(`loadFromState: snapshot contains more than ${context.maxSnapshotObjects} Fabric objects.`);
        }
    }
    const object = value;
    const type = typeof object.type === 'string' ? object.type.toLowerCase() : '';
    if (type && ALLOWED_PUBLIC_RESTORE_OBJECT_TYPES.has(type))
        return;
    if (allowEditorOwnedCustomMask && isPublicRestoreEditorOwnedCustomMaskPayload(object))
        return;
    const typePath = path ? `${path}.type` : 'type';
    if (!type) {
        throw new StateRestoreError(`loadFromState: snapshot field "${typePath}" must be a supported Fabric type.`);
    }
    throw new StateRestoreError(`loadFromState: snapshot field "${typePath}" has unsupported Fabric type "${String(object.type)}".`);
}
function shouldValidatePublicRestoreNestedFabricObject(key, value) {
    if (PUBLIC_RESTORE_FABRIC_OBJECT_KEYS.has(key))
        return true;
    return isPublicRestoreImageSourceKey(key) && hasFabricObjectType(value);
}
function hasFabricObjectType(value) {
    return (!!value && typeof value === 'object' && typeof value.type === 'string');
}
function isPublicRestoreEditorOwnedCustomMaskPayload(value) {
    if (!isMaskObject(value))
        return false;
    const candidate = value;
    const expectedMaskUid = typeof candidate.maskId === 'number' ? `mask-${candidate.maskId}` : null;
    return (Number.isInteger(candidate.maskId) &&
        typeof candidate.maskId === 'number' &&
        candidate.maskId > 0 &&
        typeof candidate.maskUid === 'string' &&
        candidate.maskUid === expectedMaskUid &&
        typeof candidate.maskName === 'string' &&
        candidate.maskName.trim() !== '' &&
        typeof candidate.originalAlpha === 'number' &&
        Number.isFinite(candidate.originalAlpha));
}
function isPublicRestoreImageSourceKey(key) {
    const normalized = key.toLowerCase();
    return (PUBLIC_RESTORE_IMAGE_SOURCE_KEYS.has(normalized) ||
        normalized.endsWith('src') ||
        normalized.endsWith('source'));
}
function restoreEditorObjectPropsFromJson(canvasObjs, jsonObjs) {
    var _a, _b, _c, _d;
    const consumedMetadataIndexes = new Set();
    jsonObjs.forEach((jObj, index) => {
        if (jObj.editorObjectKind !== 'baseImage' &&
            jObj.editorObjectKind !== 'annotation' &&
            jObj.editorObjectKind !== 'session') {
            return;
        }
        const match = findCanvasObjectForJson(canvasObjs, jObj, index, consumedMetadataIndexes);
        const canvasObj = match === null || match === void 0 ? void 0 : match.object;
        if (!canvasObj)
            return;
        if (jObj.editorObjectKind === 'baseImage') {
            markBaseImageObject(canvasObj);
            return;
        }
        if (jObj.editorObjectKind === 'annotation' &&
            typeof jObj.annotationId === 'number' &&
            typeof jObj.annotationType === 'string' &&
            typeof jObj.annotationName === 'string') {
            const annotationType = jObj.annotationType === 'draw'
                ? 'draw'
                : jObj.annotationType === 'shape'
                    ? 'shape'
                    : 'text';
            const shapeAnnotationKind = jObj.shapeAnnotationKind === 'line' || jObj.shapeAnnotationKind === 'arrow'
                ? jObj.shapeAnnotationKind
                : 'rect';
            markAnnotationObject(canvasObj, {
                annotationId: jObj.annotationId,
                annotationType,
                annotationName: jObj.annotationName,
                annotationHidden: typeof jObj.annotationHidden === 'boolean' ? jObj.annotationHidden : false,
                annotationLocked: typeof jObj.annotationLocked === 'boolean' ? jObj.annotationLocked : false,
                annotationSelectable: typeof jObj.annotationSelectable === 'boolean'
                    ? jObj.annotationSelectable
                    : undefined,
                annotationEvented: typeof jObj.annotationEvented === 'boolean'
                    ? jObj.annotationEvented
                    : undefined,
                annotationHasControls: typeof jObj.annotationHasControls === 'boolean'
                    ? jObj.annotationHasControls
                    : undefined,
                annotationEditable: typeof jObj.annotationEditable === 'boolean'
                    ? jObj.annotationEditable
                    : undefined,
                shapeAnnotationKind: annotationType === 'shape' ? shapeAnnotationKind : undefined,
            });
            if (typeof jObj.overlayPersistentId === 'string') {
                canvasObj.overlayPersistentId =
                    jObj.overlayPersistentId;
            }
            if (jObj.overlayMetadata !== undefined) {
                canvasObj.overlayMetadata = jObj.overlayMetadata;
            }
            return;
        }
        if (jObj.editorObjectKind === 'session' && typeof jObj.sessionObjectType === 'string') {
            canvasObj.editorObjectKind = 'session';
            canvasObj.sessionObjectType = jObj.sessionObjectType;
        }
    });
    const consumedCanvasIndexes = new Set();
    const canvasIndexesByMaskUid = new Map();
    canvasObjs.forEach((canvasObj, index) => {
        const maskUid = canvasObj.maskUid;
        if (typeof maskUid !== 'string')
            return;
        const indexes = canvasIndexesByMaskUid.get(maskUid);
        if (indexes) {
            indexes.push(index);
        }
        else {
            canvasIndexesByMaskUid.set(maskUid, [index]);
        }
    });
    const takeUnconsumedCanvasIndex = (indexes) => {
        if (!indexes)
            return -1;
        while (indexes.length > 0) {
            const index = indexes.shift();
            if (!consumedCanvasIndexes.has(index))
                return index;
        }
        return -1;
    };
    for (const jObj of jsonObjs) {
        if (jObj.editorObjectKind !== 'mask' || typeof jObj.maskId !== 'number')
            continue;
        const jType = String((_a = jObj.type) !== null && _a !== void 0 ? _a : '');
        const jLeft = Number((_b = jObj.left) !== null && _b !== void 0 ? _b : 0);
        const jTop = Number((_c = jObj.top) !== null && _c !== void 0 ? _c : 0);
        const jUid = typeof jObj.maskUid === 'string' ? jObj.maskUid : null;
        let matchIndex = -1;
        if (jUid) {
            matchIndex = takeUnconsumedCanvasIndex(canvasIndexesByMaskUid.get(jUid));
        }
        if (matchIndex < 0) {
            matchIndex = canvasObjs.findIndex((o, index) => {
                var _a, _b;
                if (consumedCanvasIndexes.has(index))
                    return false;
                if (jType && o.type !== jType)
                    return false;
                return (Math.abs(((_a = o.left) !== null && _a !== void 0 ? _a : 0) - jLeft) < 0.5 &&
                    Math.abs(((_b = o.top) !== null && _b !== void 0 ? _b : 0) - jTop) < 0.5 &&
                    serializedTransformMatches(o, jObj));
            });
        }
        if (matchIndex < 0)
            continue;
        consumedCanvasIndexes.add(matchIndex);
        const match = canvasObjs[matchIndex];
        const maskObject = match;
        const originalStroke = 'originalStroke' in jObj
            ? jObj.originalStroke
            : undefined;
        markMaskObject(maskObject, {
            maskId: jObj.maskId,
            maskUid: typeof jObj.maskUid === 'string' ? jObj.maskUid : `mask-${jObj.maskId}`,
            maskName: typeof jObj.maskName === 'string' ? jObj.maskName : '',
            originalAlpha: typeof jObj.originalAlpha === 'number'
                ? jObj.originalAlpha
                : ((_d = maskObject.opacity) !== null && _d !== void 0 ? _d : 0.5),
            originalStroke,
            originalStrokeWidth: typeof jObj.originalStrokeWidth === 'number' ? jObj.originalStrokeWidth : undefined,
        });
        if ('originalStroke' in jObj) {
            maskObject.originalStroke = jObj.originalStroke;
        }
        if (typeof jObj.originalStrokeWidth === 'number') {
            maskObject.originalStrokeWidth = jObj.originalStrokeWidth;
        }
        if (typeof jObj.hasControls === 'boolean') {
            maskObject.hasControls = jObj.hasControls;
        }
        if (typeof jObj.selectable === 'boolean') {
            maskObject.selectable = jObj.selectable;
        }
        if (typeof jObj.strokeUniform === 'boolean') {
            maskObject.strokeUniform = jObj.strokeUniform;
        }
        if (typeof jObj.lockRotation === 'boolean') {
            maskObject.lockRotation = jObj.lockRotation;
        }
        if (typeof jObj.transparentCorners === 'boolean') {
            maskObject.transparentCorners = jObj.transparentCorners;
        }
        if (typeof jObj.borderColor === 'string') {
            maskObject.borderColor = jObj.borderColor;
        }
        if (typeof jObj.cornerColor === 'string') {
            maskObject.cornerColor = jObj.cornerColor;
        }
        if (typeof jObj.cornerSize === 'number') {
            maskObject.cornerSize = jObj.cornerSize;
        }
        if (typeof jObj.overlayPersistentId === 'string') {
            maskObject.overlayPersistentId = jObj.overlayPersistentId;
        }
        if (jObj.overlayMetadata !== undefined) {
            maskObject.overlayMetadata = jObj.overlayMetadata;
        }
    }
    jsonObjs.forEach((jObj, index) => {
        if (jObj.maskLabel !== true)
            return;
        const canvasObj = canvasObjs[index];
        if (canvasObj) {
            canvasObj.maskLabel = true;
        }
    });
}
//# sourceMappingURL=state-serializer.js.map